import axios from "axios"
import { Dispatch } from "../store.config"
import { BuyOrSell } from "../../utils/company"
import { selectFiscalYearAction } from "./fiscalYears.ducks"
import { AnyAction } from "redux"
import { NavigateFunction } from "react-router-dom"

export type GlobalSearchSource =
  | "batch_documents"
  | "full_documents"
  | "merchants"
  | "merchant_codes"
  | "accounting_key_words"
  | "accounts"
  | "journals"

export type GlobalSearchOnCompanyItemType = {
  source: GlobalSearchSource
  id: string
  data_1: string
  data_2: string | null
  data_3: string | null
  data_4: string | null
  data_5: string | null
}

const enum GlobalSearchActionsEnum {
  SET_GLOBAL_SEARCH = "GLOBAL_SEARCH/SetGlobalSearch",
  SET_GLOBAL_SEARCHED_STRING = "GLOBAL_SEARCH/SetGlobalSearchedString",

  GLOBAL_SEARCH_ON_COMPANY_ATTEMPT = "GLOBAL_SEARCH/GlobalSearchOnCompanyAttempt",
  GLOBAL_SEARCH_ON_COMPANY_SUCCESS = "GLOBAL_SEARCH/GlobalSearchOnCompanySuccess",
  GLOBAL_SEARCH_ON_COMPANY_ERROR = "GLOBAL_SEARCH/GlobalSearchOnCompanyError",

  LOAD_DATA_ATTEMPT = "GLOBAL_SEARCH/LoadDataAttempt",
  LOAD_DATA_SUCCESS = "GLOBAL_SEARCH/LoadDataSuccess",
  LOAD_DATA_ERROR = "GLOBAL_SEARCH/LoadDataERROR",
}

interface MerchantInfo {
  fiscalYearId: number
  buyOrSell: BuyOrSell
}

interface FullDocumentInfo extends MerchantInfo {
  deactivated_type: string
}

export const SetGlobalSearchAction = (payload: string) =>
  ({
    type: GlobalSearchActionsEnum.SET_GLOBAL_SEARCH,
    payload,
  } as const)
export const SetGlobalSearchedStringAction = (payload: string) =>
  ({
    type: GlobalSearchActionsEnum.SET_GLOBAL_SEARCHED_STRING,
    payload,
  } as const)
export const GlobalSearchOnCompanyAttempt = () =>
  ({
    type: GlobalSearchActionsEnum.GLOBAL_SEARCH_ON_COMPANY_ATTEMPT,
  } as const)
export const GlobalSearchOnCompanySuccess = (
  payload: GlobalSearchOnCompanyItemType[]
) =>
  ({
    type: GlobalSearchActionsEnum.GLOBAL_SEARCH_ON_COMPANY_SUCCESS,
    payload,
  } as const)
export const GlobalSearchOnCompanyError = () =>
  ({
    type: GlobalSearchActionsEnum.GLOBAL_SEARCH_ON_COMPANY_ERROR,
  } as const)

export const LoadDataAttempt = () =>
  ({
    type: GlobalSearchActionsEnum.LOAD_DATA_ATTEMPT,
  } as const)

export const LoadDataSuccess = () =>
  ({
    type: GlobalSearchActionsEnum.LOAD_DATA_SUCCESS,
  } as const)

export const LoadDataError = () =>
  ({
    type: GlobalSearchActionsEnum.LOAD_DATA_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "company-global-search.load-data.error.title",
      bodyKey: "company-global-search.load-data.error.body",
    },
  } as const)

type GlobalSearchActionType = ReturnType<
  | typeof SetGlobalSearchAction
  | typeof SetGlobalSearchedStringAction
  | typeof GlobalSearchOnCompanyAttempt
  | typeof GlobalSearchOnCompanySuccess
  | typeof GlobalSearchOnCompanyError
  | typeof LoadDataAttempt
  | typeof LoadDataSuccess
  | typeof LoadDataError
>

export interface GlobalSearchState {
  globalSearch: string
  globalSearchedString: string
  globalSearchOnCompanyStatus: "idle" | "loading" | "success" | "error"
  globalSearchOnCompanyItems: GlobalSearchOnCompanyItemType[]
  loadDataStatus: "idle" | "loading" | "success" | "error"
}

export const globalSearchInitialState: GlobalSearchState = {
  globalSearch: "",
  globalSearchedString: "",
  globalSearchOnCompanyStatus: "idle",
  globalSearchOnCompanyItems: [],
  loadDataStatus: "idle",
}

export function globalSearchReducer(
  state = globalSearchInitialState,
  action: GlobalSearchActionType
): GlobalSearchState {
  switch (action.type) {
    case GlobalSearchActionsEnum.SET_GLOBAL_SEARCH: {
      return {
        ...state,
        globalSearch: action.payload,
      }
    }
    case GlobalSearchActionsEnum.SET_GLOBAL_SEARCHED_STRING: {
      return {
        ...state,
        globalSearchedString: action.payload,
      }
    }
    case GlobalSearchActionsEnum.GLOBAL_SEARCH_ON_COMPANY_ATTEMPT: {
      return {
        ...state,
        globalSearchOnCompanyItems: [],
        globalSearchOnCompanyStatus: "loading",
      }
    }
    case GlobalSearchActionsEnum.GLOBAL_SEARCH_ON_COMPANY_SUCCESS: {
      return {
        ...state,
        globalSearchOnCompanyStatus: "success",
        globalSearchOnCompanyItems: action.payload,
      }
    }
    case GlobalSearchActionsEnum.GLOBAL_SEARCH_ON_COMPANY_ERROR: {
      return {
        ...state,
        globalSearchOnCompanyStatus: "error",
      }
    }
    case GlobalSearchActionsEnum.LOAD_DATA_ATTEMPT: {
      return {
        ...state,
        loadDataStatus: "loading",
      }
    }
    case GlobalSearchActionsEnum.LOAD_DATA_SUCCESS: {
      return {
        ...state,
        loadDataStatus: "success",
      }
    }
    case GlobalSearchActionsEnum.LOAD_DATA_ERROR: {
      return {
        ...state,
        loadDataStatus: "error",
      }
    }
    default:
      return { ...state }
  }
}

export const getGlobalSearchOnCompanyThunk =
  (companyId: number, search: string) =>
  (dispatch: Dispatch<GlobalSearchActionType>) => {
    dispatch(GlobalSearchOnCompanyAttempt())
    return axios
      .get<GlobalSearchOnCompanyItemType[]>(
        `/companies/${companyId}/global_search_on_company`,
        {
          params: { search: search },
        }
      )
      .then((response) => {
        dispatch(GlobalSearchOnCompanySuccess(response.data))
      })
      .catch(() => {
        dispatch(GlobalSearchOnCompanyError())
      })
  }

export const getFullDocumentInfoThunkAndRedirect =
  (
    path: string,
    fullDocumentId: string,
    navigate: NavigateFunction,
    search: string
  ) =>
  (dispatch: Dispatch<GlobalSearchActionType | AnyAction>) => {
    dispatch(LoadDataAttempt())
    return axios
      .get<FullDocumentInfo>(
        `/full_documents/${fullDocumentId}/get_global_search_redirection_info`
      )
      .then((response) => {
        const data = response.data
        if (data.fiscalYearId && (data.buyOrSell || data.deactivated_type)) {
          dispatch(selectFiscalYearAction(data.fiscalYearId))

          const tab = data.buyOrSell
            ? data.buyOrSell
            : data.deactivated_type === "unsupported"
            ? "other"
            : data.deactivated_type

          dispatch(LoadDataSuccess())
          navigate(`${path}/${tab}`, {
            state: {
              searchedFullDocument: search,
            },
          })
        }
      })
      .catch(() => {
        dispatch(LoadDataError())
      })
  }

export const getMerchantInfoThunkAndRedirect =
  (
    path: string,
    merchantId: string,
    navigate: NavigateFunction,
    search: string
  ) =>
  (dispatch: Dispatch<GlobalSearchActionType | AnyAction>) => {
    dispatch(LoadDataAttempt())
    return axios
      .get<MerchantInfo>(
        `/merchants/${merchantId}/get_global_search_redirection_info`
      )
      .then((response) => {
        const data = response.data
        if (data.fiscalYearId && data.buyOrSell) {
          dispatch(selectFiscalYearAction(data.fiscalYearId))

          dispatch(LoadDataSuccess())
          navigate(`${path}/${data.buyOrSell}`, {
            state: {
              searchedMerchant: search,
            },
          })
        }
      })
      .catch(() => {
        dispatch(LoadDataError())
      })
  }
